/**
 * Frontend Entity Type Registry
 * Provides UI components and formatting for all entity types
 */

import {
  ConfirmationNumber as TicketIcon,
  Email as EmailIcon,
  InsertDriveFile as DriveIcon,
} from "@mui/icons-material";

class EntityTypeRegistry {
  constructor() {
    this.strategies = new Map();
    this.registerDefaultStrategies();
  }

  registerDefaultStrategies() {
    // Ticket Strategy
    this.register('ticket', {
      displayName: 'Ticket',
      icon: TicketIcon,
      getIcon: () => TicketIcon,
    });

    this.register('email_thread', {
      displayName: 'Email',
      icon: EmailIcon,
      getIcon: () => EmailIcon,
    });

    this.register('drive', {
      displayName: 'Drive Item',
      icon: DriveIcon,
      getIcon: () => DriveIcon,
    });

    this.register('drive_attachment', {
      displayName: 'Drive Attachment',
      icon: DriveIcon,
      getIcon: () => DriveIcon,
    });
    this.register('email_attachment', {
      displayName: 'Email Attachment',
      icon: EmailIcon,
      getIcon: () => EmailIcon,
    });
    this.register('ticket_attachment', {
      displayName: 'Ticket Attachment',
      icon: TicketIcon,
      getIcon: () => TicketIcon,
    });
  }

  /**
   * Register a new entity type strategy
   * @param {string} entityType - Type identifier
   * @param {Object} strategy - Strategy object with displayName, icon, getIcon
   */
  register(entityType, strategy) {
    this.strategies.set(entityType, strategy);
  }

  /**
   * Get strategy for an entity type
   * @param {string} entityType - Type identifier
   * @returns {Object} Strategy object
   */
  get(entityType) {
    const strategy = this.strategies.get(entityType);
    if (!strategy) {
      console.warn(`Unknown entity type: '${entityType}'`);
      return { displayName: 'Unknown', icon: null };
    }
    return strategy;
  }

  /**
   * Get icon component for an entity type
   * @param {string} entityType - Type identifier
   * @returns {Component} MUI Icon component
   */
  getIcon(entityType) {
    return this.get(entityType).icon;
  }

  /**
   * Get display name for an entity type
   * @param {string} entityType - Type identifier
   * @returns {string} Human-readable entity type name
   */
  getDisplayName(entityType) {
    return this.get(entityType).displayName;
  }

  /**
   * Check if an entity type is registered
   * @param {string} entityType - Type identifier
   * @returns {boolean}
   */
  has(entityType) {
    return this.strategies.has(entityType);
  }

  /**
   * Get all registered entity types
   * @returns {string[]} Array of entity type identifiers
   */
  getAllTypes() {
    return Array.from(this.strategies.keys());
  }
}

// Singleton instance
const entityTypeRegistry = new EntityTypeRegistry();

export { EntityTypeRegistry, entityTypeRegistry };
